import { useState } from 'react';
import type { BoardSnapshot } from '@nerds-at-play/contracts';
import { parseTeamResponse, teamAssistantPrompt, type TeamFormKind } from './team-assistant.js';
export function TeamAssistantExchange({
  kind,
  board,
  teamId,
  values,
  onFill,
}: {
  kind: TeamFormKind;
  board: BoardSnapshot;
  teamId: string;
  values: Record<string, string>;
  onFill: (fields: Record<string, string>) => void;
}) {
  const [prompt, setPrompt] = useState(() => teamAssistantPrompt(kind, board, teamId, values));
  const [response, setResponse] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  return (
    <details className="team-assistant-exchange">
      <summary>Use your own chatbot · prompt and autofill</summary>
      <p>
        Copy the prompt into your personal chatbot, then paste its formatted answer below. The
        prompt includes your current draft and authorized game context, which may contain PRIVATE
        information. You can edit it before copying. Anything you paste into that service is handled
        under its policies.
      </p>
      <label htmlFor="team-chatbot-prompt">Prompt for your chatbot</label>
      <textarea
        id="team-chatbot-prompt"
        rows={10}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
      />
      <div className="board-dialog-actions">
        <button
          type="button"
          onClick={() => {
            void (async () => {
              try {
                await navigator.clipboard.writeText(prompt);
                setError('');
                setStatus('Prompt copied. Paste it into your chatbot.');
              } catch {
                setStatus('');
                setError('Copy unavailable. Select the prompt and copy it manually.');
              }
            })();
          }}
        >
          Copy prompt
        </button>
        <button
          type="button"
          onClick={() => {
            setPrompt(teamAssistantPrompt(kind, board, teamId, values));
            setStatus('Prompt refreshed from the current form and game context.');
            setError('');
          }}
        >
          Refresh prompt from form
        </button>
      </div>
      <label htmlFor="team-chatbot-response">Paste chatbot response</label>
      <textarea
        id="team-chatbot-response"
        rows={10}
        value={response}
        onChange={(e) => {
          setResponse(e.target.value);
          setError('');
        }}
        placeholder="Paste the completed Markdown with its ## field headings here."
      />
      <button
        type="button"
        onClick={() => {
          try {
            const result = parseTeamResponse(kind, response);
            onFill(result.values);
            setError('');
            setStatus(
              `Filled ${Object.keys(result.values).length} fields. ${result.missing.length ? `Unfilled sections left unchanged: ${result.missing.join(', ')}. ` : ''}Review the form below, then save or submit when ready. Nothing has been saved or submitted.`,
            );
          } catch (e) {
            setError(
              e instanceof Error
                ? e.message
                : 'Could not read this response. Your form is unchanged.',
            );
            setStatus('');
          }
        }}
      >
        Fill form from response
      </button>
      {status && <p role="status">{status}</p>}
      {error && <p role="alert">{error}</p>}
    </details>
  );
}
